import { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner, BlockStack, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { cancelFileSigning } from "services/files-category.service";
import { refreshRequested } from "containers/unsigned-files/unsigned-files.slice";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { CancelSigningResult } from "types/Files";
import { classifyCancelSigningResult } from "./cancel-signing-outcome";
import { toast } from "sonner";

interface FileSigningCancelConfirmProps {
  fileId: string;
  companyId: string;
  fileName?: string;
  /** Rows still waiting for a signature (pending / rejected / blank). */
  awaitingCount?: number;
  /** Rows already collected — these are kept. */
  collectedCount?: number;
  onDone?: () => void;
}

/**
 * "Atšaukti pasirašymą" — drop every outstanding signature request on a
 * document at once, so it leaves the unsigned-documents list. Signatures
 * already collected are kept. Once someone has signed, the backend withdraws
 * an uploaded document instead; a work-security or module document whose
 * remaining signers have all signed is finalized.
 */
const FileSigningCancelConfirm: React.FC<FileSigningCancelConfirmProps> = ({
  fileId,
  companyId,
  fileName,
  awaitingCount = 0,
  collectedCount = 0,
  onDone,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  // The company of the page, not of the document: the list shows documents of every company.
  const { companyId: pageCompanyId } = useParams();

  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleConfirm = async () => {
    if (!companyId || !fileId) return;
    try {
      setIsLoading(true);
      setErrMsg(null);
      const result: CancelSigningResult = await cancelFileSigning(
        companyId,
        fileId
      );
      const outcome = classifyCancelSigningResult(result);

      // The endpoint answers 200 even when it removed nothing (every request
      // was classed "retryable" — e.g. a Dokobit outage). Reporting success
      // there is what made users press the button again and again, so treat it
      // as a failure: show the banner and keep the modal open for a retry.
      if (outcome.kind === "failed") {
        setErrMsg("cancel_signing_failed");
        // Deliberately no refresh: the backend removed nothing, so the list is
        // unchanged and a reload would only churn the page behind the still-open
        // modal.
        return;
      }

      if (outcome.kind === "withdrawn") {
        toast.success(t("cancel_signing_withdrawn"));
      } else if (outcome.kind === "session_gone") {
        // Cancellation went through, but the collected signatures can never be
        // sealed into this document — say so instead of a bare "cancelled".
        toast.success(t("cancel_signing_session_gone"));
      } else if (outcome.kind === "partial") {
        // Some requests were dropped, some stayed — a partial result, not a win.
        toast.warning(
          t("cancel_signing_partial", {
            removed: outcome.removed,
            total: outcome.total,
          })
        );
      } else if (outcome.kind === "completed") {
        toast.success(t("cancel_signing_completed"));
      } else {
        toast.success(t("cancel_signing_success"));
      }

      // The list page reloads itself through refreshNonce; the bell and the
      // «waiting for others» card ask the summary of the page's company again
      // (asking for the document's company would swap the summary the page shows).
      dispatch(refreshRequested());
      if (pageCompanyId) dispatch(fetchCabinetSummaryThunk(pageCompanyId));
      onDone?.();
      closeModal();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.message || e?.message || "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
      </div>
      <BlockStack gap="400">
        <h1 style={{ textAlign: "center" }}>
          {t("cancel_signing_confirm", { name: fileName || "" })}
        </h1>

        <Text as="p" tone="subdued">
          {t("cancel_signing_hint")}
        </Text>

        {/* Only says what holds for every document: the collected signatures
            stay. What happens next depends on the document (an uploaded one is
            withdrawn, a Tvarkos or module document completes), which only the
            server knows, so the outcome toast tells it after the cancel. */}
        {collectedCount > 0 && (
          <Banner tone="info">
            {t("cancel_signer_request_signatures_kept", {
              signed: collectedCount,
              total: collectedCount + awaitingCount,
            })}
          </Banner>
        )}

        <InlineStack align="space-between">
          <Button
            onClick={handleConfirm}
            loading={isLoading}
            tone="critical"
            variant="primary"
          >
            {t("Yes")}
          </Button>

          <Button onClick={closeModal}>{t("No")}</Button>
        </InlineStack>
      </BlockStack>
    </>
  );
};

export default FileSigningCancelConfirm;
